import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useDeletePlaylistMutation,
  useGetPlaylistByIdQuery,
  useGetPlaylistsQuery,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import { selectCurrentPlaylistId } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { SxProps, Theme } from '@mui/material';

import { PlaylistTaskList, usePlaylistTaskList } from './PlaylistTaskList';
import { PlaylistViewDrawerHeader } from '../atoms/PlaylistViewDrawerHeader';
import { DRAWER_MAX_WIDTH } from '../constants/priorities.constants';
import { useGetTaskActionsAndProperties } from '../hooks/tasks.hooks';
import { getPlaybookTaskPath, getPlaylistPercentageFilled } from '../utils/playlist.utils';

type PlaylistViewDrawerProps = Readonly<{
  handleClose: () => void;
}>;

export const PlaylistViewDrawer = ({ handleClose }: PlaylistViewDrawerProps) => {
  const navigate = useNavigate();
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const currentPlaylistId = useAppSelector(selectCurrentPlaylistId);
  const { data: playlists } = useGetPlaylistsQuery(undefined);

  // Fallback: if not found in list (e.g., admin endpoint), fetch by id
  const { data: playlistById } = useGetPlaylistByIdQuery(
    { id: currentPlaylistId || 0 },
    {
      skip: !currentPlaylistId,
    },
  );

  const currentPlaylist =
    playlists?.find((playlist) => playlist.id === currentPlaylistId) || playlistById || null;

  const [deletePlaylist] = useDeletePlaylistMutation();
  const { handleOpenNewTaskDialog } = useGetTaskActionsAndProperties();

  const taskList = usePlaylistTaskList(currentPlaylistId, !!currentPlaylist?.is_template);

  if (!currentPlaylist) {
    return null;
  }

  const handleDeletePlaylist = () => {
    deletePlaylist(currentPlaylist.id);
    setConfirmDeleteOpen(false);
    handleClose();
  };

  return (
    <>
      <PlaylistViewDrawerHeader
        currentPlaylist={currentPlaylist}
        confirmDeleteOpen={confirmDeleteOpen}
        setConfirmDeleteOpen={setConfirmDeleteOpen}
        handleDeletePlaylist={handleDeletePlaylist}
        sortAnchorEl={sortAnchorEl}
        setSortAnchorEl={setSortAnchorEl}
        handleOpenNewTaskDialog={handleOpenNewTaskDialog}
        handleClose={handleClose}
        percentageFilled={getPlaylistPercentageFilled(currentPlaylist)}
        wrappedSortMenuItems={taskList.wrappedSortMenuItems}
        isReorderMode={taskList.isReorderMode}
        handleToggleReorderMode={taskList.handleToggleReorderMode}
      />

      <PlaylistTaskList
        playlist={currentPlaylist}
        state={taskList}
        isInSidebar
        onOpenTask={(task) => {
          handleClose();
          navigate(
            getPlaybookTaskPath(
              task.id,
              currentPlaylist.is_template ? currentPlaylist.id : undefined,
            ),
          );
        }}
        containerSx={drawerListSx}
      />
    </>
  );
};

const drawerListSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: {
    xs: `calc(100vw - (${backrs.spacing['spacing-4xl']}))`,
    sm: `calc(${DRAWER_MAX_WIDTH}px - (${backrs.spacing['spacing-2xl']}))`,
  },
  px: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-xl'],
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});
